import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { Button, Stack } from '@mui/material'
import { OverlayState } from '@/hooks/useOverlay'
import {
  useCreateDrChronoAuthTokens,
  useCreateOrUpdateAjeraCredentialDetails,
  useCreateOrUpdateEntrataAuthTokens,
  useUpdateDrChronoAuthTokens,
} from '@/service-library/hooks/org-credentials'
import queryKeys from '@/service-library/query-keys'
import { AuthType, OrgCredential, OrgCredentialType } from '@/types/credentials'
import { authLabelMap } from '@/utils/org-credentials'
import { showErrorSnackbar, showSuccessSnackbar } from '@/utils/snackbars'
import { Dialog, DialogContent, DialogFooter } from '@/components/dialog'
import { FormTextField, PixyDocsForm } from '@/components/forms'

type FormValues = {
  client_id: string
  client_secret: string
  bearer_token: string
  url: string
  username: string
  password: string
}

type ManageCredentialAuthDialogProps = {
  authType: AuthType
  isEdit: boolean
  overlay: OverlayState
  orgCredential: OrgCredential
  type: OrgCredentialType
  onSave?: () => void
}

const defaultValues = {
  client_id: '',
  client_secret: '',
  token: '',
  url: '',
  username: '',
  password: '',
}

export default function ManageCredentialAuthDialog({
  authType,
  isEdit,
  overlay,
  orgCredential,
  type,
  onSave,
}: ManageCredentialAuthDialogProps) {
  const methods = useForm<FormValues>({
    defaultValues,
  })

  const { formState, reset } = methods

  const { createDrChronoAuthTokens } = useCreateDrChronoAuthTokens({
    sideEffectQueryKeys: [queryKeys.orgCredentials.all],
    onSuccess: () => {
      showSuccessSnackbar('Tokens Added')
      onSave?.()
    },
    onError: () => {
      showErrorSnackbar('Failed to add tokens. Please contact support.')
    },
  })

  const { updateDrChronoAuthTokens } = useUpdateDrChronoAuthTokens({
    sideEffectQueryKeys: [queryKeys.orgCredentials.all],
    onSuccess: () => {
      showSuccessSnackbar('Updated Tokens')
      onSave?.()
    },
    onError: () => {
      showErrorSnackbar('Failed to update credential. Please contact support.')
    },
  })

  const { createOrUpdateEntrataAuthTokens } =
    useCreateOrUpdateEntrataAuthTokens({
      sideEffectQueryKeys: [queryKeys.orgCredentials.all],
      onSuccess: () => {
        showSuccessSnackbar(isEdit ? 'Updated Token' : 'Token Added')
        onSave?.()
      },
      onError: () => {
        showErrorSnackbar(
          `Failed to ${
            isEdit ? 'update' : 'add'
          } token. Please contact support`,
        )
      },
    })

  const { createOrUpdateAjeraCredentialDetails } =
    useCreateOrUpdateAjeraCredentialDetails({
      sideEffectQueryKeys: [queryKeys.orgCredentials.all],
      onSuccess: () => {
        showSuccessSnackbar(isEdit ? 'Details Updated' : 'Details Added')
        onSave?.()
      },
      onError: () => {
        showErrorSnackbar(
          `Failed to ${
            isEdit ? 'update' : 'add'
          } details. Please contact support`,
        )
      },
    })

  function handleCreateCredential(values: FormValues) {
    if (type.code === 'dr_chrono') {
      if (isEdit) {
        updateDrChronoAuthTokens({
          id: orgCredential.id,
          org_id: orgCredential.org_id,
          name: orgCredential.name,
          client_id: values.client_id,
          client_secret: values.client_secret,
        })
      } else {
        createDrChronoAuthTokens({
          id: orgCredential.id,
          org_id: orgCredential.org_id,
          name: orgCredential.name,
          client_id: values.client_id,
          client_secret: values.client_secret,
        })
      }
    } else if (type.code === 'entrata_api_token') {
      createOrUpdateEntrataAuthTokens({
        org_credential_id: orgCredential.id,
        bearer_token: values.bearer_token,
      })
    } else if (type.code === 'ajera') {
      createOrUpdateAjeraCredentialDetails({
        org_credential_id: orgCredential.id,
        url: values.url,
        username: values.username,
        password: values.password,
      })
    }

    overlay.close()
  }

  const { isSubmitSuccessful, isSubmitting, isValid } = formState

  useEffect(() => {
    if (isSubmitSuccessful || (!isSubmitting && !overlay.isOpen)) {
      reset()
    }
  }, [isSubmitSuccessful, isSubmitting, overlay.isOpen, reset])

  return (
    <Dialog
      title={`${isEdit ? 'Edit' : 'Set'} ${authLabelMap[authType]}`}
      {...overlay}
      maxWidth="xs"
    >
      <PixyDocsForm methods={methods} onSubmit={handleCreateCredential}>
        <DialogContent>
          <Stack spacing={2}>
            {authType === 'secret' && (
              <>
                <FormTextField name="client_id" label="Client ID" required />
                <FormTextField
                  name="client_secret"
                  label="Client Secret"
                  required
                />
              </>
            )}
            {authType === 'token' && (
              <FormTextField name="bearer_token" label="Token" required />
            )}
            {authType === 'basic_with_url' && (
              <>
                <FormTextField
                  name="url"
                  label="API URL"
                  required
                  helperText={`You can usually find this URL in the API settings in ${type.name}.`}
                />
                <FormTextField name="username" label="Username" required />
                <FormTextField
                  autoComplete="off"
                  name="password"
                  label="Password"
                  required
                  type="password"
                />
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogFooter>
          <Button variant="text" onClick={overlay.close}>
            Cancel
          </Button>
          <Button disabled={!isValid} type="submit">
            Save
          </Button>
        </DialogFooter>
      </PixyDocsForm>
    </Dialog>
  )
}
